import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  Download,
  Eye,
  Code2,
  Save,
  XCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { supabase, invokeFunction } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import { SortableBlock } from '@/components/editor/SortableBlock';
import { BlockEditor } from '@/components/editor/BlockEditor';
import { AlertTriangle } from 'lucide-react';
import type {
  Workbook,
  WorkbookProfile,
  Order,
  Block,
  GenerationMetadata,
  GenerationWarning,
} from '@ellie/shared';

interface WorkbookWithRelations extends Workbook {
  workbook_profiles: WorkbookProfile | null;
  orders: Order | null;
}

export function WorkbookEditorPage() {
  const { id } = useParams<{ id: string }>();

  const [workbook, setWorkbook] = useState<WorkbookWithRelations | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    async function fetchWorkbook() {
      if (!id) return;

      const { data, error: fetchError } = await supabase
        .from('workbooks')
        .select('*, workbook_profiles(*), orders(*)')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Failed to fetch workbook:', fetchError.message);
        setError('Failed to load workbook.');
        setLoading(false);
        return;
      }

      const wb = data as WorkbookWithRelations;
      setWorkbook(wb);
      setBlocks(wb.blocks);
      setAdminNotes(wb.admin_notes ?? '');
      setLoading(false);
    }

    fetchWorkbook();
  }, [id]);

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) ?? null;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setBlocks((prev) => {
        const oldIndex = prev.findIndex((b) => b.id === active.id);
        const newIndex = prev.findIndex((b) => b.id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex);
        // Update order property to match new positions
        return reordered.map((b, i) => ({ ...b, order: i + 1 }));
      });
    }
  }

  function handleBlockChange(updated: Block) {
    setBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }

  function handleBlockDelete(blockId: string) {
    setBlocks((prev) => {
      const filtered = prev.filter((b) => b.id !== blockId);
      return filtered.map((b, i) => ({ ...b, order: i + 1 }));
    });
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }

  async function handleSave() {
    if (!id) return;
    setSaving(true);

    const { error: updateError } = await supabase
      .from('workbooks')
      .update({ blocks, admin_notes: adminNotes || null })
      .eq('id', id);

    if (updateError) {
      console.error('Failed to save workbook:', updateError.message);
      setError('Failed to save changes.');
    }

    setSaving(false);
  }

  async function handleDownloadPdf() {
    if (!id || !workbook) return;
    setDownloadingPdf(true);

    try {
      // Save blocks first so the PDF reflects the latest edits
      await supabase
        .from('workbooks')
        .update({ blocks, admin_notes: adminNotes || null })
        .eq('id', id);

      // Use order_id for production workbooks (pre-caches PDF for delivery),
      // workbook_id for studio workbooks
      const body = workbook.orders
        ? { order_id: workbook.orders.id }
        : { workbook_id: workbook.id };

      const { data, error: fnError } = await invokeFunction<{
        signed_url: string;
      }>('render-pdf', { body });

      if (fnError) {
        throw new Error(fnError.message ?? 'PDF rendering failed');
      }

      // Fetch as blob — browsers ignore `download` on cross-origin URLs
      // (like Supabase Storage signed URLs), so we create a local object URL
      const pdfResponse = await fetch(data.signed_url);
      if (!pdfResponse.ok) throw new Error('Failed to download PDF');
      const blob = await pdfResponse.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${workbook.title ?? 'ellie-workbook'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('PDF download failed:', err);
      setError(`PDF download failed: ${message}`);
    } finally {
      setDownloadingPdf(false);
    }
  }

  async function handleApprove() {
    if (!workbook || !workbook.orders) return;
    const confirmed = window.confirm('Approve this workbook? It will proceed to delivery.');
    if (!confirmed) return;

    setSaving(true);

    // Save blocks first
    await supabase
      .from('workbooks')
      .update({ blocks, admin_notes: adminNotes || null })
      .eq('id', id);

    // Update order status
    const { error: approveError } = await supabase
      .from('orders')
      .update({ status: 'approved', approved_at: new Date().toISOString() })
      .eq('id', workbook.orders.id);

    if (approveError) {
      console.error('Failed to approve workbook:', approveError.message);
      setError('Failed to approve workbook.');
    } else {
      setWorkbook((prev) =>
        prev && prev.orders
          ? {
              ...prev,
              orders: {
                ...prev.orders,
                status: 'approved' as const,
                approved_at: new Date().toISOString(),
              },
            }
          : prev,
      );

      logAudit({
        action: 'approve_workbook',
        resourceType: 'order',
        resourceId: workbook.orders.id,
        metadata: { workbook_id: workbook.id, previous_status: 'review' },
      });

      // Trigger delivery pipeline (fire-and-forget)
      try {
        await invokeFunction('deliver-workbook', {
          body: { order_id: workbook.orders!.id },
        });
      } catch (deliverErr) {
        console.error('Delivery trigger failed:', deliverErr);
        // Don't block — delivery can be retried from admin
      }
    }

    setSaving(false);
  }

  async function handleReject() {
    if (!workbook || !workbook.orders || !adminNotes.trim()) return;

    setSaving(true);

    // Save admin notes
    await supabase.from('workbooks').update({ admin_notes: adminNotes }).eq('id', id);

    // Update order status
    const { error: rejectError } = await supabase
      .from('orders')
      .update({ status: 'rejected' })
      .eq('id', workbook.orders.id);

    if (rejectError) {
      console.error('Failed to reject workbook:', rejectError.message);
      setError('Failed to reject workbook.');
    } else {
      setWorkbook((prev) =>
        prev && prev.orders
          ? {
              ...prev,
              orders: { ...prev.orders, status: 'rejected' as const },
            }
          : prev,
      );

      logAudit({
        action: 'reject_workbook',
        resourceType: 'order',
        resourceId: workbook.orders.id,
        metadata: {
          workbook_id: workbook.id,
          previous_status: 'review',
          admin_notes: adminNotes,
        },
      });
    }

    setShowRejectModal(false);
    setSaving(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  if (error && !workbook) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (!workbook) return null;

  const isStudio = workbook.is_studio;
  const isReviewable = !isStudio && workbook.orders?.status === 'review';

  return (
    <div className="flex h-screen flex-col">
      {/* Header bar */}
      <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
        <div>
          <h1 className="font-heading text-lg text-text-primary">{workbook.title}</h1>
          <p className="text-xs text-text-muted">
            {workbook.theme} &middot;{' '}
            {isStudio ? (
              <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                STUDIO
              </span>
            ) : (
              <>
                Status: <span className="font-medium">{workbook.orders?.status ?? '—'}</span>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/workbook/${id}/preview`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50"
          >
            <Eye size={16} />
            Preview
          </Link>

          <Link
            to={`/workbook/${id}/preview?tab=html`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50"
          >
            <Code2 size={16} />
            HTML Preview
          </Link>

          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || saving}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {downloadingPdf ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                Rendering…
              </>
            ) : (
              <>
                <Download size={16} />
                Download PDF
              </>
            )}
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>

          {isReviewable && (
            <>
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                <XCircle size={16} />
                Reject
              </button>

              <button
                onClick={handleApprove}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle size={16} />
                Approve
              </button>
            </>
          )}
        </div>
      </header>

      {/* Error banner */}
      {error && <div className="bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

      {/* Two-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — Block list */}
        <aside className="w-80 shrink-0 overflow-y-auto border-r border-border bg-white p-4">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
            Blocks ({blocks.length})
          </h2>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {blocks.map((block) => (
                  <SortableBlock
                    key={block.id}
                    block={block}
                    isSelected={block.id === selectedBlockId}
                    onSelect={setSelectedBlockId}
                    onDelete={handleBlockDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Admin notes */}
          <div className="mt-6">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Admin Notes
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              placeholder="Internal notes about this workbook..."
              className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>

          {/* Generation metadata */}
          <div className="mt-6">
            <button
              onClick={() => setShowMetadata(!showMetadata)}
              className="flex w-full items-center gap-1 text-xs font-semibold uppercase tracking-wider text-text-muted hover:text-text-primary"
            >
              {showMetadata ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Generation Info
            </button>
            {showMetadata && <GenerationMetadataPanel workbook={workbook} />}
          </div>
        </aside>

        {/* Right panel — Block editor */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedBlock ? (
            <BlockEditor block={selectedBlock} onChange={handleBlockChange} />
          ) : (
            <div className="flex h-full items-center justify-center text-center">
              <div>
                <p className="text-lg font-medium text-text-muted">No block selected</p>
                <p className="mt-1 text-sm text-text-light">
                  Select a block from the list to edit it.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <h2 className="mb-4 font-heading text-lg text-text-primary">Reject Workbook</h2>
            <p className="mb-3 text-sm text-text-muted">
              Provide notes explaining the reason for rejection. These will be saved with the
              workbook.
            </p>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={4}
              placeholder="Reason for rejection..."
              className="mb-4 w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowRejectModal(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!adminNotes.trim() || saving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generation Metadata Panel
// ---------------------------------------------------------------------------

function GenerationMetadataPanel({ workbook }: { workbook: WorkbookWithRelations }) {
  const meta = workbook.generation_metadata as GenerationMetadata | null;
  const warnings = (workbook.generation_warnings ?? []) as GenerationWarning[];

  function formatDuration(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60_000);
    const secs = Math.round((ms % 60_000) / 1000);
    return `${mins}m ${secs}s`;
  }

  return (
    <div className="mt-2 space-y-3">
      {/* Summary row */}
      <div className="rounded-md bg-gray-50 p-2.5">
        <dl className="space-y-1.5">
          {[
            [
              'Model',
              meta?.provider
                ? `${meta.provider}/${workbook.generation_model}`
                : (workbook.generation_model ?? '—'),
            ],
            ['Prompt', workbook.generation_prompt_version ?? '—'],
            [
              'Duration',
              workbook.generation_duration_ms
                ? formatDuration(workbook.generation_duration_ms)
                : '—',
            ],
            ['Blocks', `${workbook.block_count ?? '—'}`],
            ['Pages', `${workbook.page_count ?? '—'}`],
          ].map(([label, value]) => (
            <div key={label} className="flex items-center justify-between text-xs">
              <dt className="text-text-muted">{label}</dt>
              <dd className="font-medium text-text-primary">{value}</dd>
            </div>
          ))}
          {meta && (
            <>
              {[
                ['Input tokens', meta.input_tokens.toLocaleString()],
                ['Output tokens', meta.output_tokens.toLocaleString()],
                ['API calls', `${meta.api_calls}`],
                ['AI cost', `$${meta.estimated_cost_usd.toFixed(4)}`],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <dt className="text-text-muted">{label}</dt>
                  <dd className="font-medium text-text-primary">{value}</dd>
                </div>
              ))}
            </>
          )}
        </dl>
      </div>

      {/* Block health */}
      {meta && meta.blocks_succeeded != null && meta.blocks_total != null && (
        <div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-muted">Block success:</span>
            <span
              className={`font-medium ${
                meta.blocks_succeeded === meta.blocks_total ? 'text-green-600' : 'text-amber-600'
              }`}
            >
              {meta.blocks_succeeded}/{meta.blocks_total}
            </span>
            {meta.block_retries != null && meta.block_retries > 0 && (
              <span className="text-text-light">({meta.block_retries} retries)</span>
            )}
          </div>

          {meta.back_matter && (
            <div className="mt-1.5 flex gap-2 text-[11px]">
              {(['verb_glossary', 'cultural_notes', 'welcome_page'] as const).map((key) => {
                const status = meta.back_matter![key];
                const label = key.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase());
                return (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-0.5 ${
                      status === 'ok' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {status === 'ok' ? '✓' : '✗'} {label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Batch timings chart */}
      {meta?.batch_timings_ms && meta.batch_timings_ms.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-medium text-text-muted">Batch Timings</p>
          <div className="space-y-1">
            {meta.batch_timings_ms.map((ms, i) => {
              const maxMs = Math.max(...meta.batch_timings_ms!);
              const widthPct = maxMs > 0 ? (ms / maxMs) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-1.5 text-[11px]">
                  <span className="w-10 shrink-0 text-text-muted">B{i + 1}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded bg-gray-100">
                    <div
                      className="h-full rounded bg-purple-400"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right font-mono text-text-muted">
                    {(ms / 1000).toFixed(1)}s
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div>
          <p className="mb-1.5 flex items-center gap-1 text-[11px] font-medium text-amber-600">
            <AlertTriangle size={11} />
            {warnings.length} Warning{warnings.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-1">
            {warnings.map((w, i) => (
              <div key={i} className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                <span className="font-medium">{w.component}:</span> {w.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {!meta && (
        <p className="text-xs italic text-text-light">
          No cost data — generated before tracking was added.
        </p>
      )}
    </div>
  );
}
